import { api, delay, streamSSE, USE_MOCK } from "../client";
import type { NotificationItem, NotificationListResult } from "@/features/notifications/types";

export type NotificationListParams = {
  limit?: number;
  unreadOnly?: boolean;
  before?: string;
};

// ── Mock data (USE_MOCK) — a small mutable set so mark-read reflects in the UI ──
let _mock: NotificationItem[] | null = null;
function mockNotifications(): NotificationItem[] {
  if (_mock) return _mock;
  const now = Date.now();
  const iso = (minsAgo: number) => new Date(now - minsAgo * 60_000).toISOString();
  _mock = [
    {
      id: "n1", type: "business_critical_email", tone: "attention",
      title: "Business-critical email: Thai Union PO exception",
      body: "Awaiting your review — shipment at risk.",
      link: "/inbox/thread-1", meta: {}, readAt: null, createdAt: iso(12),
    },
    {
      id: "n2", type: "chat_complete", tone: "done",
      title: "Ask Ubik finished — result ready",
      body: "Your pricing report PDF is ready.",
      link: "/chat", meta: {}, readAt: null, createdAt: iso(38),
    },
    {
      id: "n3", type: "integration_failure", tone: "warn",
      title: "Zoho disconnected — reconnect needed",
      body: "Reconnect it in Integrations to resume ERP sync.",
      link: "/integrations/zoho", meta: {}, readAt: iso(59), createdAt: iso(60),
    },
  ];
  return _mock;
}

export const notificationsApi = {
  async list(params?: NotificationListParams): Promise<NotificationListResult> {
    if (USE_MOCK) {
      await delay();
      const all = mockNotifications();
      const items = params?.unreadOnly ? all.filter((n) => !n.readAt) : all;
      return { items, unreadCount: all.filter((n) => !n.readAt).length };
    }
    const q = new URLSearchParams();
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.unreadOnly) q.set("unread_only", "true");
    if (params?.before) q.set("before", params.before);
    const qs = q.toString();
    return api.get<NotificationListResult>(`/v1/notifications${qs ? `?${qs}` : ""}`);
  },

  async unreadCount(): Promise<number> {
    if (USE_MOCK) {
      await delay();
      return mockNotifications().filter((n) => !n.readAt).length;
    }
    const res = await api.get<{ count: number }>("/v1/notifications/unread_count");
    return res.count;
  },

  async markRead(id: string): Promise<void> {
    if (USE_MOCK) {
      await delay();
      const n = mockNotifications().find((x) => x.id === id);
      if (n && !n.readAt) n.readAt = new Date().toISOString();
      return;
    }
    await api.post(`/v1/notifications/${id}/read`, {});
  },

  async dismiss(id: string): Promise<void> {
    if (USE_MOCK) {
      await delay();
      _mock = mockNotifications().filter((n) => n.id !== id);
      return;
    }
    await api.delete(`/v1/notifications/${id}`);
  },

  async markAllRead(): Promise<void> {
    if (USE_MOCK) {
      await delay();
      const nowIso = new Date().toISOString();
      mockNotifications().forEach((n) => { if (!n.readAt) n.readAt = nowIso; });
      return;
    }
    await api.post("/v1/notifications/read_all", {});
  },

  /**
   * Create a notification for the current user (self-emit). Used for
   * client-detected, time-based events the backend can't see — the 5-min
   * upcoming-meeting reminder. `dedupKey` keeps it once-per-occurrence.
   * Returns whether a fresh row was created (false = deduped).
   */
  async selfEmit(payload: {
    type: string;
    title: string;
    body?: string;
    link?: string;
    tone?: string;
    meta?: Record<string, unknown>;
    dedupKey?: string;
  }): Promise<{ created: boolean }> {
    if (USE_MOCK) {
      await delay();
      const all = mockNotifications();
      if (payload.dedupKey && all.some((n) => !n.readAt && n.meta?.dedupKey === payload.dedupKey)) {
        return { created: false };
      }
      all.unshift({
        id: `local-${Date.now()}`,
        type: payload.type,
        title: payload.title,
        body: payload.body ?? null,
        link: payload.link ?? null,
        tone: (payload.tone as NotificationItem["tone"]) ?? "info",
        meta: { ...(payload.meta ?? {}), dedupKey: payload.dedupKey },
        readAt: null,
        createdAt: new Date().toISOString(),
      });
      return { created: true };
    }
    const res = await api.post<{ ok: boolean; created: boolean }>("/v1/notifications/self", {
      type: payload.type,
      title: payload.title,
      body: payload.body,
      link: payload.link,
      tone: payload.tone ?? "info",
      meta: payload.meta,
      dedup_key: payload.dedupKey,
    });
    return { created: !!res.created };
  },

  /**
   * Long-lived SSE stream of NEW notifications for the current user. Uses the
   * shared `streamSSE` (fetch + ReadableStream) so the X-Session-Token header is
   * sent — a native EventSource can't do that. One-shot per call: the caller
   * (use-notifications-stream) reconnects when it ends (Cloud Run caps the
   * connection at its request timeout).
   */
  streamNotifications(onEvent: (n: NotificationItem) => void, signal: AbortSignal): Promise<void> {
    if (USE_MOCK) {
      // No push in mock mode — the react-query poll keeps the bell populated.
      return Promise.resolve();
    }
    return streamSSE(
      "/v1/notifications/stream",
      {},
      (json) => {
        try {
          onEvent(JSON.parse(json) as NotificationItem);
        } catch {
          /* ignore malformed frames */
        }
      },
      signal,
    );
  },
};
